"use client";

import Image from "next/image";
import styles from "./listing-card-premium.module.css";
import {
  MdArrowBackIosNew,
  MdArrowForwardIos,
  MdBathtub,
  MdBed,
  MdEventAvailable,
  MdFavorite,
  MdFavoriteBorder,
  MdLocationOn,
  MdMeetingRoom,
  MdOutlineArchitecture,
  MdOutlineTram,
  MdOutlineVideoCameraBack,
  MdPhotoLibrary,
  MdPictureAsPdf,
  MdSquareFoot,
  MdSubway,
} from "react-icons/md";
import {
  FaBus,
  FaCompass,
  FaEye,
  FaHospital,
  FaLocationDot,
} from "react-icons/fa6";
import { IoChatboxEllipses } from "react-icons/io5";
import { motion } from "framer-motion";
import {
  ApiError,
  ContactDialogType,
  Currencies,
  ListingCardData,
  ListingTypeEnum,
  TypeEnum,
} from "@/types/types";
import { useLocale, useTranslations } from "next-intl";
import { useUserAuthDataContext } from "@/global-context/user-auth-context";
import { m2ToSqft } from "@/lib/sqftToM2";
import { useCurrenciesContext } from "@/global-context/currencies-context";
import { formatPrice } from "@/lib/price-formatter";
import { Swiper, SwiperSlide, useSwiper } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import { Pagination } from "swiper/modules";
import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { DeviceType } from "@/hooks/use-device-type";
import { usePageStore } from "../../usePageStore";
import { toast } from "react-toastify";
import { useListingsCacheStore } from "@/global-cache/useListingsCacheStore";
import { useAnimationStore } from "../useHasBeenAnimatedStore";
import { env } from "@/lib/env";
import { BsBadge3D } from "react-icons/bs";
import { useIsMobile } from "@/hooks/useIsMobile";
import { getLocalizedPath } from "@/lib/localizedRoutes/routeHelpers";
import ContactDialog from "@/components/contact-agent-info/seller-contact-dialog";
import { resolveCardSavedError } from "@/lib/resolve-listing-card-saved-state";
import { useScrollLock } from "@/hooks/use-scroll-lock";
import { getMedia } from "@/lib/media/shared"; // <-- NEW IMPORT
import { formatAvailableFrom } from "@/lib/formatAvailableFrom";
import { useOptimalImageSize } from "@/hooks/useOptimalImageSize";

const LISTINGSENDPOINTURL = env.NEXT_LISTINGS_API_URL;

export default function ListingCardPremium({
  data,
  isResizable,
  hasActions,
  hasRoundButtons,
}: {
  data: ListingCardData;
  isResizable: boolean;
  hasActions: boolean;
  hasRoundButtons: boolean;
}) {
  const { userData } = useUserAuthDataContext();
  const { currenciesRates } = useCurrenciesContext();
  const currencyKey = `EUR-${userData.currency}` as keyof Currencies;
  const rate: number = currenciesRates[currencyKey];
  const t = useTranslations("ListingCard");
  const { deviceType, openDialog } = usePageStore();
  const locale = useLocale();
  const isSaved = useListingsCacheStore(
    (state) => state.meta[data.listing_id]?.is_saved,
  );
  const updateSavedState = useListingsCacheStore(
    (state) => state.updateSavedState,
  );
  const setPreviewListingState = usePageStore(
    (state) => state.setPreviewListingState,
  );
  const { animatedIds, markAnimated } = useAnimationStore();
  const hasBeenAnimated = animatedIds.has(data.listing_id);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoVisible, setIsVideoVisible] = useState(false);
  const pauseTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const playTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const optimalImageSize = useOptimalImageSize();

  const viewKey = data.view?.city_view
    ? "city-view"
    : data.view?.countryside_view
      ? "countryside-view"
      : data.view?.sea_view
        ? "sea-view"
        : data.view?.mountain_view
          ? "mountain-view"
          : null;

  const orietationKey = data.orientation?.north
    ? "north"
    : data.orientation?.south
      ? "south"
      : data.orientation?.east
        ? "east"
        : data.orientation?.west
          ? "west"
          : null;

  const [isContactOpen, setIsContactOpen] = useState(false);
  const [isPriceRequest, setIsPriceRequest] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  useScrollLock(isContactOpen);

  const handleShowVideo = useCallback(() => {
    if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current);
    if (playTimeoutRef.current) clearTimeout(playTimeoutRef.current);

    playTimeoutRef.current = setTimeout(() => {
      setIsVideoVisible(true);
      if (videoRef.current) {
        videoRef.current.play().catch((err) => {
          if (err.name !== "AbortError") {
            console.error(err);
          }
        });
      }
    }, 400);
  }, []);

  const handleHideVideo = useCallback(() => {
    if (playTimeoutRef.current) clearTimeout(playTimeoutRef.current);
    if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current);
    pauseTimeoutRef.current = setTimeout(() => {
      if (videoRef.current) {
        videoRef.current.pause();
      }
      setIsVideoVisible(false);
    }, 200);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleEnded = () => {
      if (isVideoVisible) {
        video.currentTime = 0;
        video.play().catch((err) => {
          if (err.name !== "AbortError") {
          }
        });
      }
    };

    video.addEventListener("ended", handleEnded);

    return () => {
      video.removeEventListener("ended", handleEnded);
    };
  }, [isVideoVisible]);

  const listingUrl = getLocalizedPath(locale, "listing", [data.listing_id]);
  const handleCardClick = async () => {
    if (deviceType === DeviceType.desktop) {
      window.open(listingUrl, "_blank", "noopener,noreferrer");
    } else {
      window.history.pushState(null, "", listingUrl);
      await openDialog(data.listing_id, locale, userData.sub);
    }
  };

  const scrollRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);
  const hasMoved = useRef(false);
  const pointerDownCoords = useRef<{ x: number; y: number } | null>(null);
  const isDraggingCard = useRef(false);

  useEffect(() => {
    const slider = scrollRef.current;
    if (!slider) return;

    const handlePointerDown = (e: PointerEvent) => {
      isDragging.current = true;
      startX.current = e.pageX;
      scrollLeft.current = slider.scrollLeft;
      hasMoved.current = false;
      slider.setPointerCapture(e.pointerId);
    };

    const handlePointerMove = (e: PointerEvent) => {
      if (!isDragging.current) return;
      const x = e.pageX;
      const walk = x - startX.current;
      if (Math.abs(walk) > 1) hasMoved.current = true;
      slider.scrollLeft = scrollLeft.current - walk;
    };

    const handlePointerUp = (e: PointerEvent) => {
      isDragging.current = false;
      slider.releasePointerCapture(e.pointerId);
    };

    const handleClickCapture = (e: MouseEvent) => {
      if (hasMoved.current) {
        e.stopPropagation();
        e.preventDefault();
      }
    };

    slider.addEventListener("pointerdown", handlePointerDown);
    slider.addEventListener("pointermove", handlePointerMove);
    slider.addEventListener("pointerup", handlePointerUp);
    slider.addEventListener("click", handleClickCapture, true);

    return () => {
      slider.removeEventListener("pointerdown", handlePointerDown);
      slider.removeEventListener("pointermove", handlePointerMove);
      slider.removeEventListener("pointerup", handlePointerUp);
      slider.removeEventListener("click", handleClickCapture, true);
    };
  }, []);

  const [hasInteracted, setHasInteracted] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile(1024);

  const handleCardPointerDown = (e: React.PointerEvent) => {
    pointerDownCoords.current = { x: e.clientX, y: e.clientY };
    isDraggingCard.current = false;
  };

  const handleCardPointerUp = (e: React.PointerEvent) => {
    if (pointerDownCoords.current) {
      const dx = Math.abs(e.clientX - pointerDownCoords.current.x);
      const dy = Math.abs(e.clientY - pointerDownCoords.current.y);

      // If pointer moved more than 10px, flag it as a drag
      if (dx > 10 || dy > 10) {
        isDraggingCard.current = true;
      }
    }
  };

  const handleSmartClick = async (
    e: React.MouseEvent | React.KeyboardEvent,
  ) => {
    e.stopPropagation();
    e.preventDefault();

    if (isDraggingCard.current) {
      isDraggingCard.current = false;
      return;
    }

    await handleCardClick();
  };

  useEffect(() => {
    if (!isMobile || !carouselRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (!hasInteracted) {
              handleShowVideo();
            }
          } else {
            if (playTimeoutRef.current) clearTimeout(playTimeoutRef.current);
            handleHideVideo();
            setHasInteracted(false);
          }
        });
      },
      { threshold: 0.8 },
    );

    observer.observe(carouselRef.current);

    return () => {
      observer.disconnect();
      if (playTimeoutRef.current) clearTimeout(playTimeoutRef.current);
      if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current);
    };
  }, [hasInteracted, isMobile]);

  const handleUserInteraction = () => {
    setHasInteracted(true);
    handleHideVideo();
  };
  // --- NEW: Convert Photo Record to Array ---
  const { previewPhotos, totalPhotosCount } = useMemo(() => {
    const allPhotos = Object.entries(data.photos || {})
      .sort((a, b) => Number(a[0]) - Number(b[0]))
      .map(([_, item]) => item);

    const total = allPhotos.length;
    const cappedPhotos = allPhotos.slice(0, 10);

    const resolved = cappedPhotos.map((photo) => {
      // Feed optimalImageSize directly into getMedia
      const resolvedMedia = getMedia(photo, optimalImageSize);
      const displaySrc =
        resolvedMedia?.src || photo.localUrl || "/broken_image.webp";
      const displayWebp = resolvedMedia?.webp;

      return {
        ...photo,
        resolvedMedia,
        displaySrc,
        displayWebp,
      };
    });

    return { previewPhotos: resolved, totalPhotosCount: total };
  }, [data.photos, optimalImageSize]);

  const floorPlansArray = useMemo(() => {
    return Object.values(data.photos || {}).filter(
      (photo) => photo.room_tag === "floor_plans",
    );
  }, [data.photos]);

  // --- NEW: Resolve Video ---
  const resolvedVideo = useMemo(() => {
    return getMedia(data.presentation_video_data, "md");
  }, [data.presentation_video_data]);
  const resolvedBrochure = useMemo(() => {
    return getMedia(data.brochure, "md");
  }, [data.presentation_video_data]);

  return (
    <motion.article
      initial={!hasBeenAnimated ? { opacity: 0 } : false}
      animate={!hasBeenAnimated ? { opacity: 1 } : false}
      transition={{
        duration: 0.3,
        delay: 0.0,
        ease: "easeIn",
      }}
      onAnimationComplete={() => {
        if (!hasBeenAnimated) markAnimated(data.listing_id);
      }}
    >
      <a
        href={listingUrl} // Natively allows right-click & middle-click
        draggable={false} // <--- THE MAGIC FIX: Prevents breaking your scroll list
        className={`${styles.cardWrapper} ${
          hasActions && styles.actionsLayout
        } ${isResizable && styles.resizable} ${
          data.listing_type === ListingTypeEnum.SPOTLIGHT && styles.spotlight
        }`}
        /* (Keep the rest of your exact props!) */
        role="link"
        tabIndex={0}
        onPointerDown={handleCardPointerDown}
        onPointerUp={handleCardPointerUp}
        onClick={handleSmartClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            handleSmartClick(e);
          }
        }}
      >
        <div
          ref={carouselRef}
          className={styles.imageCarousel}
          onTouchStart={(e) => {
            handleUserInteraction();
          }}
        >
          {data.available_from && (
            <div className={styles.floatingAvailableBadge}>
              <MdEventAvailable />
              <span>
                {t("from")} {formatAvailableFrom(data.available_from, locale)}
              </span>
            </div>
          )}
          <nav aria-label="Media controls">
            <ul className={styles.buttonsList}>
              {/* PHOTOS */}
              {previewPhotos.length > 0 && (
                <li>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setPreviewListingState(
                        data,
                        false,
                        false,
                        false,
                        true,
                        false,
                        false,
                      );
                    }}
                    aria-label={t("viewPhotos")}
                  >
                    <MdPhotoLibrary aria-hidden="true" />
                  </button>
                </li>
              )}

              {/* FLOOR PLANS */}
              {floorPlansArray.length > 0 && (
                <li>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setPreviewListingState(
                        data,
                        false,
                        false,
                        false,
                        false,
                        true,
                        false,
                      );
                    }}
                    aria-label={t("viewFloorPlans")}
                  >
                    <MdOutlineArchitecture aria-hidden="true" />
                  </button>
                </li>
              )}

              {/* VIDEO */}
              {resolvedVideo?.src && data.has_presentation_video && (
                <li>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setPreviewListingState(
                        data,
                        true,
                        false,
                        false,
                        false,
                        false,
                        false,
                      );
                    }}
                    aria-label={t("playVideo")}
                  >
                    <MdOutlineVideoCameraBack aria-hidden="true" />
                  </button>
                </li>
              )}

              {/* 3D TOUR */}
              {data.web_tour_url && data.has_web_tour && (
                <li>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setPreviewListingState(
                        data,
                        false,
                        true,
                        false,
                        false,
                        false,
                        false,
                      );
                    }}
                    aria-label={t("startTour")}
                  >
                    <BsBadge3D aria-hidden="true" />
                  </button>
                </li>
              )}

              {/* BROCHURE */}
              {data.has_brochure && resolvedBrochure?.src && (
                <li>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setPreviewListingState(
                        data,
                        false,
                        false,
                        false,
                        false,
                        false,
                        true,
                      );
                    }}
                    aria-label={t("viewBrochure")}
                  >
                    <MdPictureAsPdf aria-hidden="true" />
                  </button>
                </li>
              )}

              {/* MAP */}
              <li>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setPreviewListingState(
                      data,
                      false,
                      false,
                      true,
                      false,
                      false,
                      false,
                    );
                  }}
                  aria-label={t("locationOnMap")}
                >
                  <MdLocationOn aria-hidden="true" />
                </button>
              </li>
            </ul>
          </nav>

          {/* BACKGROUND HOVER VIDEO */}
          {resolvedVideo?.src && (
            <video
              ref={videoRef}
              src={resolvedVideo.src}
              className={`${styles.videoPlayer} ${
                !isVideoVisible && styles.hidden
              }`}
              muted
              playsInline
              preload="none"
              aria-hidden="true"
            />
          )}

          <figure
            className={styles.carouselWrapper}
            aria-label={t("photoGallery")}
          >
            <Swiper
              loop={false}
              resistance={true}
              resistanceRatio={0}
              spaceBetween={0}
              slidesPerView={1}
              modules={[Pagination]}
              pagination={{ clickable: true, dynamicBullets: true }}
              className={styles.swiperComponent}
            >
              {previewPhotos.length > 0 ? (
                previewPhotos.map((photoItem, index) => {
                  // Check if this is exactly the 10th photo (index 9) AND there are more photos left
                  const isLastCappedSlide =
                    index === 9 && totalPhotosCount > 10;

                  return (
                    <SwiperSlide
                      key={photoItem.MediaId || index}
                      className={styles.swiperSlideComponent}
                      role="group"
                      aria-roledescription="slide"
                      aria-label={`${t("image")} ${index + 1} ${t("of")} ${previewPhotos.length}`}
                    >
                      {/* NEW WRAPPER FOR OVERLAY POSITIONING */}
                      <div className={styles.imageContainer}>
                        <picture style={{ display: "contents" }}>
                          {photoItem.displayWebp && (
                            <source
                              srcSet={photoItem.displayWebp}
                              type="image/webp"
                            />
                          )}
                          <Image
                            loading={index !== 0 ? "lazy" : undefined}
                            className={styles.image}
                            src={photoItem.displaySrc}
                            width={1200}
                            height={675}
                            unoptimized={true}
                            alt={`${t("propertyPhoto")} ${index + 1}`}
                            draggable={false}
                            priority={index === 0}
                            placeholder="blur"
                            blurDataURL="/placeholder_image.jpg"
                            fetchPriority={index === 0 ? "high" : "auto"}
                          />
                        </picture>

                        {/* THE 10TH PHOTO OVERLAY */}
                        {isLastCappedSlide && (
                          <div
                            className={styles.seeAllOverlay}
                            onClick={handleSmartClick}
                          >
                            <div className={styles.overlayContent}>
                              <span className={styles.overlayPlus}>
                                +{totalPhotosCount - 10}
                              </span>
                              <span className={styles.overlayText}>
                                {t("seeAllPhotos") || "See All Photos"}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </SwiperSlide>
                  );
                })
              ) : (
                <SwiperSlide className={styles.swiperSlideComponent}>
                  <Image
                    className={styles.image}
                    src="/broken_image.webp"
                    width={1200}
                    height={675}
                    unoptimized={true}
                    alt="No photos available"
                    draggable={false}
                  />
                </SwiperSlide>
              )}
              <NextAndBackButtons />
            </Swiper>
          </figure>
        </div>
        <div
          className={styles.contentWrapper}
          onMouseEnter={handleShowVideo}
          onMouseLeave={handleHideVideo}
          onFocus={handleShowVideo}
        >
          <div className={styles.topContent}>
            <header>
              <h2 className={styles.title}>{data.title}</h2>
            </header>
            {data.short_description?.length > 0 && (
              <p className={styles.shortDescription}>
                {data.short_description}
              </p>
            )}
            <div ref={scrollRef} className={styles.detailsRow}>
              <dl className={styles.details}>
                <dt className={styles.srOnly}>{t("bedrooms")}</dt>
                <dd className={styles.fullText}>
                  <MdBed aria-hidden="true" className={styles.icon} />
                  <span>
                    {data.number_of_bedrooms || "-"} {t("bedrooms")}
                  </span>
                </dd>
              </dl>
              <div className={styles.separator}></div>
              <dl className={styles.details}>
                <dt className={styles.srOnly}>{t("bathrooms")}</dt>
                <dd className={styles.fullText}>
                  <MdBathtub aria-hidden="true" className={styles.icon} />
                  <span>
                    {data.number_of_bathrooms || "-"} {t("bathrooms")}
                  </span>
                </dd>
              </dl>
              <div className={styles.separator}></div>
              <dl className={styles.details}>
                <dt className={styles.srOnly}>{t("rooms")}</dt>
                <dd className={styles.fullText}>
                  <MdMeetingRoom aria-hidden="true" className={styles.icon} />
                  <span>
                    {data?.number_of_rooms || "-"} {t("rooms")}
                  </span>
                </dd>
              </dl>
              <div className={styles.separator}></div>
              <dl className={styles.details}>
                <dt className={styles.srOnly}>{t("area")}</dt>
                <dd className={styles.fullText}>
                  <MdSquareFoot aria-hidden="true" className={styles.icon} />
                  {data.type !== TypeEnum.LAND ? (
                    <span>
                      {userData.unit_system === "Metric"
                        ? data.interior_space
                        : m2ToSqft(data.interior_space) || "-"}{" "}
                      {userData.unit_system === "Metric" ? "sqm" : "sqft"}
                    </span>
                  ) : (
                    <span>
                      {userData.unit_system === "Metric"
                        ? data.lot_size
                        : m2ToSqft(data.lot_size) || "-"}{" "}
                      {userData.unit_system === "Metric" ? "sqm" : "sqft"}
                    </span>
                  )}
                </dd>
              </dl>
              {viewKey !== null && (
                <>
                  <div className={styles.separator}></div>
                  <dl className={styles.details}>
                    <dt className={styles.srOnly}>{viewKey && t(viewKey)}</dt>
                    <dd className={styles.fullText}>
                      <FaEye aria-hidden="true" className={styles.icon} />
                      <span>{viewKey && t(viewKey)}</span>
                    </dd>
                  </dl>
                </>
              )}
              {orietationKey !== null && (
                <>
                  <div className={styles.separator}></div>
                  <dl className={styles.details}>
                    <dt className={styles.srOnly}>
                      {orietationKey && t(orietationKey)}
                    </dt>
                    <dd className={styles.fullText}>
                      <FaCompass aria-hidden="true" className={styles.icon} />
                      <span>
                        {t("orientated")} {orietationKey && t(orietationKey)}
                      </span>
                    </dd>
                  </dl>
                </>
              )}
            </div>
            <address className={styles.location}>
              <FaLocationDot className={styles.icon} aria-hidden="true" />
              <span className={styles.address}>{data.address}</span>
            </address>
          </div>
          <span
            className={styles.price}
            aria-label={
              data.price_on_request
                ? t("findPrice") // Make sure to add this to your translation files!
                : `${t("price")} ${formatPrice(rate * data.price)} ${userData.currency}${
                    data?.purpose === "for-rent"
                      ? data.is_price_per_year
                        ? ` per year`
                        : ` per month`
                      : ""
                  }`
            }
          >
            {data.price_on_request ? (
              <button
                className={styles.findPriceButton}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsPriceRequest(true);
                  setIsContactOpen(true);
                }}
              >
                {t("findPrice")}
              </button>
            ) : (
              <>
                <strong>
                  {t("price")} {formatPrice(rate * data.price)}{" "}
                  {userData.currency}{" "}
                </strong>
                {data?.purpose === "for-rent" &&
                  (data.is_price_per_year
                    ? `/ ${t("year")}`
                    : `/ ${t("month")}`)}
              </>
            )}
          </span>
          <div className={styles.userDetailsAndActions}>
            <div className={styles.userDetailsWrapper}>
              <figure>
                <Image
                  className={styles.userProfilePhoto}
                  src={
                    data.creator?.photo_url
                      ? data.creator.photo_url
                      : "/broken_image.webp"
                  }
                  width={200}
                  height={200}
                  alt={`${t("userProfilePhoto")} ${data.creator?.name || ""}`}
                  draggable={false}
                  priority={true}
                  placeholder="blur"
                  blurDataURL="/placeholder_image.jpg"
                />
              </figure>
              <p className={styles.userName}>{data.creator?.name}</p>
            </div>
            {hasRoundButtons && (
              <div className={styles.actionButtons}>
                <button
                  aria-label={
                    isSaved ? t("removeFromFavorites") : t("addToFavorites")
                  }
                  onKeyDown={(e) => {
                    e.stopPropagation();
                  }}
                  onClick={async (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    if (userData.sessionId === null) {
                      toast(
                        "Please reauthenticate before performing this action.",
                        { type: "error" },
                      );
                      return;
                    }
                    if (isSaved) {
                      updateSavedState(data.listing_id, false);
                      try {
                        const response = await fetch(
                          `${LISTINGSENDPOINTURL}/removeFromSavedListings`,
                          {
                            method: "POST",
                            headers: {
                              "Content-Type": "application/json",
                              Authorization: userData.idToken || "",
                            },
                            body: JSON.stringify({
                              listing_id: data.listing_id,
                            }),
                          },
                        );
                        if (response.ok) return;

                        const apiErr = (await response.json()) as ApiError;
                        resolveCardSavedError({
                          apiErr: apiErr,
                          listingId: data.listing_id,
                          attemptedState: false,
                          updateSavedState: updateSavedState,
                        });
                        throw new Error(apiErr.message);
                      } catch (err) {
                        const errorMessage =
                          err instanceof Error
                            ? err.message
                            : "An unexpected error occurred.";
                        toast(
                          errorMessage === "Failed to fetch"
                            ? "Unable to save this listing to database."
                            : errorMessage,
                          { type: "error" },
                        );
                        return;
                      }
                    } else {
                      updateSavedState(data.listing_id, true);
                      try {
                        const response = await fetch(
                          `${LISTINGSENDPOINTURL}/addToSavedListings`,
                          {
                            method: "POST",
                            headers: {
                              "Content-Type": "application/json",
                              Authorization: userData.idToken || "",
                            },
                            body: JSON.stringify({
                              listing_id: data.listing_id,
                            }),
                          },
                        );
                        if (response.ok) return;

                        const apiErr = (await response.json()) as ApiError;
                        resolveCardSavedError({
                          apiErr: apiErr,
                          listingId: data.listing_id,
                          attemptedState: true,
                          updateSavedState: updateSavedState,
                        });
                        throw new Error(apiErr.message);
                      } catch (err) {
                        const errorMessage =
                          err instanceof Error
                            ? err.message
                            : "An unexpected error occurred.";
                        toast(
                          errorMessage === "Failed to fetch"
                            ? "Unable to unsave this listing from database."
                            : errorMessage,
                          { type: "error" },
                        );
                        return;
                      }
                    }
                  }}
                  className={styles.roundButton}
                >
                  {isSaved ? (
                    <MdFavorite
                      className={`${styles.icon} ${styles.heartActive}`}
                    />
                  ) : (
                    <MdFavoriteBorder className={styles.icon} />
                  )}
                </button>
                <button
                  aria-label={t("sendMessageToUser", {
                    userName: data.creator?.name,
                  })}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setIsPriceRequest(false);
                      setIsContactOpen(true);
                    }
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setIsPriceRequest(false);
                    setIsContactOpen(true);
                  }}
                  className={styles.roundButton}
                >
                  <IoChatboxEllipses className={styles.icon} />
                </button>
              </div>
            )}
          </div>
        </div>
      </a>
      {isContactOpen && (
        <ContactDialog
          type={ContactDialogType.LISTING}
          userSub={data.created_by}
          userType={data.creator.user_type}
          photoUrl={data.creator.photo_url}
          userName={data.creator.name}
          listingId={data.listing_id}
          listingTitle={data.title}
          closeCallback={() => {
            setIsPriceRequest(false);
            setIsContactOpen(false);
          }}
          phoneNumber={data.creator.phone_number}
          hasWhatsAppActive={data.creator.has_whatsapp_active}
          dialogRef={dialogRef}
          isPriceRequest={isPriceRequest}
        />
      )}
    </motion.article>
  );
}

export function ListingCardLoading({
  isResizable,
  hasActions,
  hasRoundButtons,
  hasStatusLabel,
  hasModifyButton,
}: {
  isResizable: boolean;
  hasActions: boolean;
  hasRoundButtons: boolean;
  hasStatusLabel: boolean;
  hasModifyButton: boolean;
}) {
  return (
    <div
      className={`${styles.loadingWrapper} ${isResizable && styles.resizable}`}
    >
      <div
        className={`${styles.cardWrapper} ${
          hasActions && styles.actionsLayout
        } ${isResizable && styles.resizable} ${styles.isCardLoading}`}
      >
        {/* <div className={styles.demoLabel}>Demo</div> */}
        <div className={`${styles.imageCarousel} ${styles.isLoading}`}></div>
        <div
          className={`${styles.contentWrapper} ${styles.contentWrapperLoading}`}
        >
          <div className={styles.topContent}>
            <h2 className={`${styles.title} ${styles.isLoading}`}></h2>
            {isResizable && (
              <h2
                className={`${styles.title} ${styles.isLoading} ${styles.bigLayout}`}
                style={{ width: "60%" }}
              ></h2>
            )}
            <span className={styles.detailsRow}>
              <span className={`${styles.details} ${styles.isLoading}`}></span>
              <div className={styles.separator}></div>
              <span className={`${styles.details} ${styles.isLoading}`}></span>
            </span>
            <span className={styles.detailsRow}>
              <span className={`${styles.details} ${styles.isLoading}`}></span>
              <div className={styles.separator}></div>
              <span className={`${styles.details} ${styles.isLoading}`}></span>
            </span>
            <ul className={styles.proximityWrapper}>
              {[...Array(5)].map((_, i) => {
                return (
                  <li
                    key={i}
                    className={`${styles.proximityPill} ${styles.isLoading}`}
                  ></li>
                );
              })}
            </ul>
            <span className={styles.location}>
              <span className={styles.isLoading}></span>
            </span>
          </div>
          <div
            className={`${styles.bottomWrapper} ${
              hasRoundButtons && styles.hasRoundButtons
            }`}
          >
            <span className={`${styles.price} ${styles.isLoading}`}></span>
            {hasStatusLabel && (
              <div
                className={`${styles.statusLabelWrapper} ${styles.isLoading}`}
              ></div>
            )}
            {hasModifyButton && (
              <button
                className={`${styles.modifyButton} ${styles.isLoading}`}
              ></button>
            )}
            {hasRoundButtons && (
              <div className={styles.actionButtons}>
                <button
                  className={`${styles.roundButton} ${styles.isLoading}`}
                ></button>
                <button
                  className={`${styles.roundButton} ${styles.isLoading}`}
                ></button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export interface PreviewButtonParam {
  hasButton: boolean;
  onClick: () => void;
}

function NextAndBackButtons() {
  const swiper = useSwiper();
  const [isBeginning, setIsBeginning] = useState(swiper.isBeginning);
  const [isEnd, setIsEnd] = useState(swiper.isEnd);

  useEffect(() => {
    // Listen to swiper events to update our button visibility state
    const updateNavigation = () => {
      setIsBeginning(swiper.isBeginning);
      setIsEnd(swiper.isEnd);
    };

    swiper.on("slideChange", updateNavigation);

    // Cleanup listener on unmount
    return () => {
      swiper.off("slideChange", updateNavigation);
    };
  }, [swiper]);

  return (
    <>
      {!isBeginning && (
        <button
          className={styles.backButton}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            swiper.slidePrev();
          }}
        >
          <MdArrowBackIosNew />
        </button>
      )}
      {!isEnd && (
        <button
          className={styles.nextButton}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            swiper.slideNext();
          }}
        >
          <MdArrowForwardIos />
        </button>
      )}
    </>
  );
}

// "school":                   "school",
// "underground_train-subway": "subwayStation",
// "hospital":                 "hospital",
// "bus_stop":                 "busStation",
// "fitness-health_club":      "fitnessClub",
// "park-recreation_area":     "park",
// "lightrail":                "lightRailStation",
